import { useEffect, useRef, useState } from "react";

/**
 * Animated counter from 0 to `end` (numeric prefix preserved).
 * Accepts strings like "10+", "24/7", "100%" — animates numeric portion.
 * Re-animates whenever `target` changes (e.g. after an async API load).
 */
export function useCountUp(target, { duration = 1600, start = true } = {}) {
  const [display, setDisplay] = useState(typeof target === "string" ? "0" : 0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!start) return undefined;

    const str = String(target ?? 0);
    const match = str.match(/(\d+)/);
    if (!match) {
      setDisplay(str);
      return undefined;
    }
    const numStr = match[1];
    const numEnd = parseInt(numStr, 10);
    const prefix = str.slice(0, match.index);
    const suffix = str.slice(match.index + numStr.length);

    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      const v = Math.round(numEnd * eased);
      setDisplay(`${prefix}${v}${suffix}`);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, start]);

  return display;
}
